import { useState, useEffect } from 'react';
import { Sun, Moon, X } from 'lucide-react';

interface LetterFocusViewProps {
  content: string;
  subject?: string | null;
  date?: string;
  isOpen: boolean;
  onClose: () => void;
}

function getWordCount(text: string): number {
  const normalized = text.trim();
  if (!normalized) return 0;
  return normalized.split(/\s+/).filter(Boolean).length;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * A focused reading view for letters with light/dark themes
 * Designed as an immersive reading experience with parchment styling
 */
export function LetterFocusView({
  content,
  subject,
  date,
  isOpen,
  onClose,
}: LetterFocusViewProps) {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') return 'light';
    const saved = localStorage.getItem('letter-focus-theme');
    return saved === 'dark' ? 'dark' : 'light';
  });

  useEffect(() => {
    localStorage.setItem('letter-focus-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const wordCount = getWordCount(content);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Controls */}
        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full transition-colors ${
              theme === 'light'
                ? 'bg-stone/30 text-amber-800 hover:text-amber-950 hover:bg-stone/50'
                : 'bg-parchment/20 text-parchment/70 hover:text-parchment hover:bg-parchment/30'
            }`}
            title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          >
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </button>
          {/* Close button */}
          <button
            onClick={onClose}
            className={`p-2 rounded-full transition-colors ${
              theme === 'light'
                ? 'bg-stone/30 text-amber-800 hover:text-amber-950 hover:bg-stone/50'
                : 'bg-parchment/20 text-parchment/70 hover:text-parchment hover:bg-parchment/30'
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Letter page */}
        <div
          className={`rounded-lg shadow-2xl overflow-auto max-h-[90vh] transition-colors ${
            theme === 'light'
              ? 'bg-gradient-to-b from-amber-50 to-amber-100'
              : 'bg-gradient-to-b from-slate-900 to-slate-950'
          }`}
        >
          {/* Paper content */}
          <div className="relative px-8 py-12 sm:px-12 sm:py-16 md:px-20 md:py-20">
            {/* Decorative header line */}
            <div
              className={`absolute top-6 left-8 right-8 sm:top-8 sm:left-12 sm:right-12 md:left-20 md:right-20 h-px bg-gradient-to-r from-transparent to-transparent ${
                theme === 'light' ? 'via-amber-400/50' : 'via-slate-600'
              }`}
            />

            {/* Subject/Title */}
            {subject && (
              <h1
                className={`font-serif text-xl sm:text-2xl md:text-3xl text-center mb-6 md:mb-8 leading-relaxed ${
                  theme === 'light' ? 'text-amber-900' : 'text-parchment'
                }`}
              >
                {subject}
              </h1>
            )}

            {/* Date */}
            {date && (
              <p
                className={`text-center text-xs sm:text-sm mb-8 ${
                  theme === 'light' ? 'text-amber-700/70' : 'text-slate-400'
                }`}
              >
                {formatDate(date)}
              </p>
            )}

            {/* Letter body */}
            <div
              className={`font-serif text-base md:text-lg leading-relaxed whitespace-pre-wrap ${
                theme === 'light' ? 'text-amber-950' : 'text-slate-300'
              }`}
            >
              {content}
            </div>

            {/* Decorative footer line */}
            <div
              className={`absolute bottom-6 left-8 right-8 sm:bottom-8 sm:left-12 sm:right-12 md:left-20 md:right-20 h-px bg-gradient-to-r from-transparent to-transparent ${
                theme === 'light' ? 'via-amber-400/50' : 'via-slate-600'
              }`}
            />
          </div>
        </div>

        {/* Word count badge */}
        <div
          className={`absolute bottom-4 left-4 text-xs px-2 py-1 rounded ${
            theme === 'light'
              ? 'text-amber-800/70 bg-amber-100/80'
              : 'text-mist/70 bg-black/50'
          }`}
        >
          {wordCount} words
        </div>
      </div>
    </div>
  );
}
